<template>
  <div v-if="message.senderType === 3" class="sys-msg">
    <span class="sys-msg-pill">{{ message.content }}</span>
  </div>

  <div v-else class="bubble-row" :class="{ right: isRight }">
    <el-avatar
      :size="36"
      :src="message.senderAvatar || undefined"
      class="bubble-avatar"
      :style="message.senderAvatar ? undefined : { backgroundColor: avatarColor }"
    >
      <i :class="avatarIcon"></i>
    </el-avatar>

    <div class="bubble-body">
      <div v-if="!isRight" class="bubble-sender">{{ message.senderName }}</div>

      <div
        class="msg-bubble-content"
        :class="{ right: isRight }"
        :style="isRight ? { '--link-color': '#c7d2fe' } : { '--link-color': '#4f46e5' }"
      >
        <el-image
          v-if="message.msgType === 1"
          :src="normalizeUrl(message.content)"
          :preview-src-list="[normalizeUrl(message.content)]"
          fit="contain"
          class="bubble-image"
        />
        <a
          v-else-if="message.msgType === 2"
          :href="normalizeUrl(message.content)"
          target="_blank"
          rel="noopener noreferrer"
          :download="file.fileName"
          class="file-card"
          :class="{ right: isRight }"
        >
          <span class="file-ext" :style="{ backgroundColor: extColor }">{{ file.ext }}</span>
          <div class="file-meta">
            <div class="file-name">{{ file.fileName }}</div>
            <div v-if="file.sizeLabel" class="file-size">{{ file.sizeLabel }}</div>
          </div>
          <i class="el-icon-download file-dl"></i>
        </a>
        <SafeHtml v-else :html="message.content" />
      </div>

      <div
        v-if="isRight && customerLastReadMsgId != null && Number(message.id) <= Number(customerLastReadMsgId)"
        class="read-flag"
      >
        已读
      </div>
    </div>
  </div>
</template>

<script>
import SafeHtml from './SafeHtml.vue'

const EXT_COLORS = {
  pdf: '#e54545',
  doc: '#2b579a', docx: '#2b579a',
  xls: '#217346', xlsx: '#217346',
  ppt: '#d24726', pptx: '#d24726',
  zip: '#b58900', rar: '#b58900',
  txt: '#8c8c8c', csv: '#8c8c8c'
}

export default {
  name: 'MessageBubble',
  components: { SafeHtml },
  props: {
    message: { type: Object, required: true },
    isAgent: { type: Boolean, default: false },
    customerLastReadMsgId: { type: [Number, String], default: null }
  },
  computed: {
    isRight() {
      return this.message.senderType === 2
    },
    isCustomer() {
      return this.message.senderType === 0
    },
    isAI() {
      return this.message.senderType === 1
    },
    avatarIcon() {
      if (this.isCustomer) return 'el-icon-user'
      if (this.isAI) return 'el-icon-magic-stick'
      return 'el-icon-chat-line-round'
    },
    avatarColor() {
      if (this.isRight) return '#4f46e5'
      if (this.isAI) return '#8b5cf6'
      return '#06b6d4'
    },
    file() {
      return this.parseFileExtra(this.message)
    },
    extColor() {
      return EXT_COLORS[this.file.ext] || '#5a9fd4'
    }
  },
  methods: {
    normalizeUrl(url) {
      if (url && /^https?:\/\//.test(url) && url.includes('/uploads/')) {
        return url.replace(/^https?:\/\/[^/]+/, '')
      }
      return url
    },
    parseFileExtra(message) {
      let fileName = String(message.content).split('/').pop() || '文件'
      let fileSize = 0
      try {
        const ed = typeof message.extraData === 'string' ? JSON.parse(message.extraData) : message.extraData
        if (ed && ed.fileName) fileName = ed.fileName
        if (ed && ed.fileSize) fileSize = Number(ed.fileSize)
      } catch (e) { /* ignore */ }
      const sizeLabel = fileSize > 1024 * 1024
        ? (fileSize / 1024 / 1024).toFixed(1) + ' MB'
        : fileSize > 0
          ? (fileSize / 1024).toFixed(0) + ' KB'
          : ''
      const ext = (String(fileName).split('.').pop() || '').toLowerCase()
      return { fileName, sizeLabel, ext }
    }
  }
}
</script>

<style scoped>
.sys-msg {
  text-align: center;
  color: #8c8c8c;
  font-size: 12px;
  margin: 10px 0;
  padding: 4px 12px;
}
.sys-msg-pill {
  background: rgba(0, 0, 0, 0.04);
  padding: 3px 10px;
  border-radius: 10px;
}
.bubble-row {
  display: flex;
  align-items: flex-start;
  margin: 10px 0;
  gap: 10px;
}
.bubble-row.right {
  flex-direction: row-reverse;
}
.bubble-avatar {
  flex-shrink: 0;
}
.bubble-body {
  max-width: 65%;
}
.bubble-sender {
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 4px;
}
.msg-bubble-content {
  background: #fff;
  color: #1f2937;
  padding: 10px 14px;
  border-radius: 2px 12px 12px 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  font-size: 14px;
  line-height: 1.6;
}
.msg-bubble-content.right {
  background: #4f46e5;
  color: #fff;
  border-radius: 12px 2px 12px 12px;
}
.bubble-image {
  max-width: 200px;
  border-radius: 6px;
  cursor: pointer;
}
.file-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.03);
  text-decoration: none;
  color: inherit;
  min-width: 200px;
}
.file-card.right {
  background: rgba(255, 255, 255, 0.12);
}
.file-ext {
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  text-transform: uppercase;
  border-radius: 6px;
  padding: 8px 6px;
  min-width: 40px;
  text-align: center;
  letter-spacing: 0.5px;
}
.file-meta {
  flex: 1;
  min-width: 0;
}
.file-name {
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.file-size {
  font-size: 11px;
  opacity: 0.7;
  margin-top: 2px;
}
.file-dl {
  font-size: 16px;
  opacity: 0.6;
}
.read-flag {
  font-size: 11px;
  color: #4f46e5;
  text-align: right;
  margin-top: 2px;
}
</style>